/**
 * CustomOverlay - Google Maps OverlayView implementation
 * Based on Google Maps JavaScript API OverlayView
 *
 * Factory function that creates the CustomOverlay class only when
 * Google Maps is loaded. This is safer than conditional class extension.
 */

import {
	ANIMATION_DURATION_MS,
	ANIMATION_DURATION_OPEN_MS,
	OVERLAY_SCALE_ENTER,
	OVERLAY_SCALE_EXIT,
	DEFAULT_OVERLAY_TILE_WIDTH,
	DEFAULT_OVERLAY_TILE_HEIGHT,
	DEFAULT_PIN_WIDTH,
	DEFAULT_PIN_HEIGHT,
	DEFAULT_MAP_WIDTH,
	DEFAULT_MAP_HEIGHT
} from '../constants/mapConstants';

/**
 * Calculate the optimal position for the overlay relative to the pin
 * @param {Object} params - Position calculation parameters
 * @param {number} params.pointX - Pin X coordinate
 * @param {number} params.pointY - Pin Y coordinate
 * @param {number} params.mapWidth - Map container width
 * @param {number} params.mapHeight - Map container height
 * @param {number} params.tileWidth - Overlay tile width
 * @param {number} params.tileHeight - Overlay tile height
 * @param {number} params.pinWidth - Pin icon width
 * @param {number} params.pinHeight - Pin icon height
 * @returns {Object} Position with top and left coordinates
 */
const calculateOverlayPosition = ({
	pointX,
	pointY,
	mapWidth,
	mapHeight,
	tileWidth,
	tileHeight,
	pinWidth,
	pinHeight
}) => {
	const halfMapWidth = mapWidth / 2;
	const halfMapHeight = mapHeight / 2;
	const halfTileWidth = tileWidth / 2;

	// Calculate available space in each direction from the pin position
	const spaceBottom = halfMapHeight - pointY;
	const spaceTop = pointY + halfMapHeight - pinHeight;
	const spaceLeft = pointX + halfMapWidth - pinWidth / 2;
	const spaceRight = halfMapWidth - pinWidth / 2 - pointX;

	// Determine vertical position: prefer below, then above, then centered
	let top;
	let isVerticallyCentered = false;
	if (spaceBottom > tileHeight) {
		top = pointY;
	} else if (spaceTop > tileHeight) {
		top = pointY - tileHeight - pinHeight;
	} else {
		// Default: center vertically with the pin
		top = pointY - (tileHeight + pinHeight) / 2;
		isVerticallyCentered = true;
	}

	// Determine horizontal position: prefer centered if not vertically centered, then right, then left
	let left;
	if (
		spaceRight > halfTileWidth &&
		spaceLeft > halfTileWidth &&
		!isVerticallyCentered
	) {
		// Center horizontally if both sides have adequate space
		left = pointX - halfTileWidth;
	} else if (spaceRight > tileWidth) {
		left = pointX + pinWidth / 2;
	} else {
		left = pointX - tileWidth - pinWidth / 2;
	}

	return { top, left };
};

const createCustomOverlayClass = () => {
	const { OverlayView } = window.google.maps;

	return class CustomOverlay extends OverlayView {
		constructor(position, onClose, onContainerReady, icon = null) {
			super();
			this.position = position;
			this.icon = icon;
			this.onClose = onClose;
			this.onContainerReady = onContainerReady;
			this.div = null;
			this.isClosing = false;
			this.clickHandler = null;
			this.closeTimeout = null;
		}

		onAdd() {
			const div = document.createElement('div');
			div.style.position = 'absolute';
			div.style.zIndex = '1000';

			// Add animation styles
			div.style.opacity = '0';
			div.style.transform = `scale(${OVERLAY_SCALE_ENTER})`;
			div.style.transition = `opacity ${ANIMATION_DURATION_OPEN_MS}ms ease-out, transform ${ANIMATION_DURATION_OPEN_MS}ms ease-out`;

			// Prevent clicks from propagating to map
			this.clickHandler = (e) => {
				e.stopPropagation();
			};
			div.addEventListener('click', this.clickHandler);

			this.div = div;

			const panes = this.getPanes();
			panes.floatPane.appendChild(div);

			// Notify that container is ready for portal
			if (this.onContainerReady) {
				this.onContainerReady(div);
			}

			// Trigger animation after DOM insertion
			requestAnimationFrame(() => {
				if (this.div) {
					this.div.style.opacity = '1';
					this.div.style.transform = 'scale(1)';
				}
			});
		}

		draw() {
			if (!this.div) return;

			const projection = this.getProjection();
			if (!projection) return;

			const point = projection.fromLatLngToDivPixel(this.position);
			const map = this.getMap();
			if (!point || !this.div || !map) return;

			const mapDiv = map.getDiv();
			const mapRect = mapDiv.getBoundingClientRect();

			const mapWidth = mapRect.width ?? DEFAULT_MAP_WIDTH;
			const mapHeight = mapRect.height ?? DEFAULT_MAP_HEIGHT;

			const tileWidth =
				this.div.offsetWidth ?? DEFAULT_OVERLAY_TILE_WIDTH;
			const tileHeight =
				this.div.offsetHeight ?? DEFAULT_OVERLAY_TILE_HEIGHT;

			// Extract pin dimensions from icon or use defaults
			const pinWidth = this.icon?.scaledSize?.width ?? DEFAULT_PIN_WIDTH;
			const pinHeight =
				this.icon?.scaledSize?.height ?? DEFAULT_PIN_HEIGHT;

			// Calculate optimal position for the overlay
			const { top, left } = calculateOverlayPosition({
				pointX: point.x,
				pointY: point.y,
				mapWidth,
				mapHeight,
				tileWidth,
				tileHeight,
				pinWidth,
				pinHeight
			});

			this.div.style.top = `${top}px`;
			this.div.style.left = `${left}px`;
		}

		onRemove() {
			// Clear any pending close timeout
			if (this.closeTimeout) {
				clearTimeout(this.closeTimeout);
				this.closeTimeout = null;
			}

			if (this.div) {
				// Remove event listener to prevent memory leak
				if (this.clickHandler) {
					this.div.removeEventListener('click', this.clickHandler);
					this.clickHandler = null;
				}
				this.div.remove();
				this.div = null;
			}

			this.isClosing = false;
		}

		close() {
			if (this.isClosing || !this.div) return;

			this.isClosing = true;

			this.div.style.transition = `opacity ${ANIMATION_DURATION_MS}ms ease-in, transform ${ANIMATION_DURATION_MS}ms ease-in`;

			// Trigger fade-out animation
			this.div.style.opacity = '0';
			this.div.style.transform = `scale(${OVERLAY_SCALE_EXIT})`;

			this.closeTimeout = setTimeout(() => {
				if (this.onClose) {
					this.onClose();
				}
				this.setMap(null);
			}, ANIMATION_DURATION_MS);
		}

		getDiv() {
			return this.div;
		}
	};
};

export { calculateOverlayPosition };
export default createCustomOverlayClass;
